import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { DocumentService } from '@/services/DocumentService';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, FileText, Download, Trash2, ExternalLink } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface Document {
  id: string;
  document_name: string;
  document_type: string;
  file_path: string;
  file_size: number;
  user_id: string;
  uploaded_at: string | null;
}

export const FileUploadTest = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentLink, setDocumentLink] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState<'file' | 'link'>('file');
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<{ id: string; email: string; full_name: string }[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadUsers();
    loadDocuments();
  }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
    }
  };

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      const docs = await DocumentService.getDocuments();
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (documentType === 'file' && !documentFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    if (documentType === 'link' && !documentLink) {
      toast({
        title: "Error",
        description: "Please enter a document link",
        variant: "destructive",
      });
      return;
    }

    if (!selectedUser) {
      toast({
        title: "Error",
        description: "Please select a user to assign the document to",
        variant: "destructive",
      });
      return;
    }

    if (!documentTitle.trim()) {
      toast({
        title: "Error",
        description: "Please enter a document title",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      if (documentType === 'file' && documentFile) {
        const result = await DocumentService.uploadDocument(
          documentFile,
          documentTitle,
          'document',
          undefined,
          selectedUser
        );

        toast({
          title: "Success",
          description: "Document uploaded successfully",
        });

        // Reset form
        setDocumentFile(null);
        setDocumentTitle('');
      } else if (documentType === 'link' && documentLink) {
        // Handle link document
        const result = await DocumentService.createLinkDocument(
          documentTitle,
          documentLink,
          selectedUser
        );

        toast({
          title: "Success",
          description: "Document link added successfully",
        });

        // Reset form
        setDocumentLink('');
        setDocumentTitle('');
      }

      // Reload documents after successful upload
      loadDocuments();
    } catch (error) {
      console.error('Document upload error:', error);
      setError('Failed to upload document');
      toast({
        title: "Error",
        description: "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await DocumentService.deleteDocument(documentId);
      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
      loadDocuments(); // Reload the documents list
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown User';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Document Upload</CardTitle>
          <CardDescription>Upload documents or add document links</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user">Assign to User *</Label>
              <Select
                value={selectedUser}
                onValueChange={setSelectedUser}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Document Title *</Label>
              <Input
                id="title"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                placeholder="Enter document title"
              />
            </div>

            <Tabs defaultValue="file" className="w-full" onValueChange={(value) => setDocumentType(value as 'file' | 'link')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="file">Upload File</TabsTrigger>
                <TabsTrigger value="link">Add Link</TabsTrigger>
              </TabsList>

              <TabsContent value="file" className="space-y-4">
                <div className="space-y-2">
                  <Label>Document Upload</Label>
                  <p className="text-sm text-gray-500">Accepted formats: PDF, Word, PowerPoint, Excel, Images. Max size: 50MB</p>
                  <div className="flex items-center gap-4">
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                      onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="link" className="space-y-4">
                <div className="space-y-2">
                  <Label>Document Link</Label>
                  <p className="text-sm text-gray-500">Add a link to an external document</p>
                  <div className="flex items-center gap-4">
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={documentLink}
                      onChange={(e) => setDocumentLink(e.target.value)}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <Button 
              onClick={handleDocumentUpload} 
              disabled={isUploading || (!documentFile && !documentLink) || !documentTitle || !selectedUser}
              className="w-full"
            >
              {isUploading ? "Processing..." : "Upload Document"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Assigned Documents</CardTitle>
          <CardDescription>View and manage all assigned documents</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2">No documents assigned yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        {doc.document_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        doc.document_type === 'link' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {doc.document_type === 'link' ? 'Link' : 'File'}
                      </span>
                    </TableCell>
                    <TableCell>{getUserName(doc.user_id)}</TableCell>
                    <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                    <TableCell>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {doc.document_type === 'link' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(doc.file_path, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/api/documents/${doc.id}`, '_blank')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 